import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { addChatsToAiTestContainer, getAiTestContainerById } from "../../api/AiAgentApi";
import AddIcon from "@mui/icons-material/Add";
import { AiTestContainerModelsForm } from "./AiTestContainerModelsForm";

export default function AiTestContainerDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [container, setContainer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [addChatsOpen, setAddChatsOpen] = useState(false);
    const [newChats, setNewChats] = useState([
        {
            modelName: '',
            modelProvider: 'OPEN_AI',
        },
    ]);

    useEffect(() => {
        loadContainer();
    }, [id]);

    async function loadContainer() {
        try {
            setLoading(true);
            setError(null);
            const data = await getAiTestContainerById(id);
            setContainer(data);
        } catch (e) {
            console.error(e);
            setError('Не удалось загрузить контейнер');
        } finally {
            setLoading(false);
        }
    }

    function handleBack() {
        navigate('/ai-test-containers');
    }

    // Функция для получения цвета статуса
    const getStatusColor = (status) => {
        switch (status) {
            case 'COMPLETED':
                return 'success';
            case 'PROCESSING':
                return 'warning';
            case 'CREATED':
                return 'default';
            default:
                return 'default';
        }
    };

    // Функция для получения текста статуса на русском
    const getStatusText = (status) => {
        switch (status) {
            case 'COMPLETED':
                return 'Завершено';
            case 'PROCESSING':
                return 'В процессе';
            case 'CREATED':
                return 'Создано';
            default:
                return status;
        }
    };

    if (loading && !container) {
        return (
            <Box p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={24} />
                    <Typography>Загрузка...</Typography>
                </Stack>
            </Box>
        );
    }

    if (error && !container) {
        return (
            <Box p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography color="error">{error}</Typography>
                    <Button onClick={loadContainer}>Повторить</Button>
                </Stack>
            </Box>
        );
    }

    if (!container) {
        return null;
    }

    const chats = container.chats || [];

    async function handleSubmitAddChats() {
        try {
            const hasEmpty = newChats.some(
                (m) => !m.modelName || !m.modelProvider
            );
            if (hasEmpty) {
                alert('Заполните все модели');
                return;
            }

            await addChatsToAiTestContainer(container.id, newChats);
            setAddChatsOpen(false);

            await loadContainer();

            setNewChats([
                { modelName: '', modelProvider: 'OPEN_AI' }
            ]);
        } catch (e) {
            console.error(e);
            alert('Не удалось добавить чаты');
        }
    }

    return (
        <Box p={3} sx={{ height: '100%', boxSizing: 'border-box' }}>
            {/* Шапка */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <IconButton onClick={handleBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" fontWeight={600}>
                        AI Test Container #{container.id}
                    </Typography>
                </Stack>

                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddChatsOpen(true)}
                >
                    Добавить чаты
                </Button>
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
                ReservationId: <b>{container.reservationId}</b>
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Основной блок с чатами в колонках */}
            {chats.length === 0 ? (
                <Typography>В этом контейнере пока нет чатов</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        overflowX: 'auto',
                        pb: 2,
                    }}
                >
                    {chats.map((chat, index) => (
                        <Paper
                            key={chat.id ?? index}
                            variant="outlined"
                            sx={{
                                minWidth: 320,
                                maxWidth: 360,
                                flex: '0 0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: 'calc(100vh - 180px)', // чтобы влезало по высоте
                            }}
                        >
                            {/* Заголовок колонки */}
                            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Chat #{chat.id ?? index + 1}
                                    </Typography>
                                    <Chip
                                        label={getStatusText(chat.status)}
                                        size="small"
                                        color={getStatusColor(chat.status)}
                                        variant="outlined"
                                    />
                                </Stack>
                                <Stack direction="column" spacing={0.5} mt={1}>
                                    <Typography variant="body2">
                                        modelName: <b>{chat.modelName}</b>
                                    </Typography>
                                    <Typography variant="body2">
                                        modelProvider: <b>{chat.modelProvider}</b>
                                    </Typography>
                                    {chat.status === 'PROCESSING' && (
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <CircularProgress size={12} />
                                            <Typography variant="caption" color="text.secondary">
                                                Обработка...
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>

                            {/* Сообщения, вертикальный скролл */}
                            <Box
                                sx={{
                                    p: 2,
                                    overflowY: 'auto',
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                {(!chat.messages || chat.messages.length === 0) && (
                                    <Box textAlign="center" py={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            Сообщений пока нет
                                        </Typography>
                                        {chat.status === 'CREATED' && (
                                            <Typography variant="caption" color="text.secondary">
                                                Чат создан, но диалог не начат
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {chat.messages?.map((m) => {
                                    const isBot = m.role === 'BOT';

                                    return (
                                        <Stack
                                            key={m.id}
                                            direction="row"
                                            justifyContent={isBot ? 'flex-end' : 'flex-start'}
                                        >
                                            <Box
                                                sx={{
                                                    maxWidth: '80%',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                {/* Метаданные над пузырём (роль + время) */}
                                                <Stack
                                                    direction="row"
                                                    justifyContent={isBot ? 'flex-end' : 'flex-start'}
                                                    sx={{ mb: 0.5 }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isBot ? 'BOT' : 'USER'} ·{' '}
                                                        {m.createdAt ? new Date(m.createdAt).toLocaleTimeString() : ''}
                                                    </Typography>
                                                </Stack>

                                                {/* Пузырь сообщения */}
                                                <Box
                                                    sx={{
                                                        px: 1.5,
                                                        py: 1,
                                                        borderRadius: 3,
                                                        bgcolor: isBot ? 'primary.main' : 'grey.200',
                                                        color: isBot ? 'primary.contrastText' : 'text.primary',
                                                        // скругления как в мессенджерах
                                                        borderTopRightRadius: isBot ? 0 : 24,
                                                        borderTopLeftRadius: isBot ? 24 : 0,
                                                        boxShadow: 1,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        {m.messageText}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    );
                                })}
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}


            <Dialog
                open={addChatsOpen}
                onClose={() => setAddChatsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Добавить чаты в контейнер #{container.id}</DialogTitle>
                <DialogContent dividers>
                    <AiTestContainerModelsForm
                        models={newChats}
                        onModelsChange={setNewChats}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddChatsOpen(false)}>Отмена</Button>
                    <Button variant="contained" onClick={handleSubmitAddChats}>
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}